/**
 * <s-terrain-briefing> — "Le briefing terrain" camera finder (blocks/terrain-briefing.liquid).
 *
 * Markup contract:
 *   [data-step="1|2|3"] panes with [data-answer] tiles (aria-pressed) and a [data-question] heading
 *   [data-back], [data-restart]  — navigation
 *   [data-track="1|2|3"]          — step tracker items; data-state = current | done | todo
 *   [data-result-area]            — result; [data-result="fullhd|lcd|wifi|4g"] panels, [data-notes] slot,
 *                                   [data-note="…"] notes (moved between [data-note-holder] and the active panel), [data-recap-list]
 *   [data-add]                    — AJAX add to cart (data-variant-id, data-product-url fallback)
 *   [data-briefing-live]          — polite live region
 * Answers are kept in sessionStorage (data-storage-key) to show the result again on return.
 */

const STEP3_KEYS = ['oui', 'faible', 'non', 'nsp'];

/** Decision rule (the 3 questions are always asked): base = Q2 key, then the network / remote-land exceptions. */
export function resolveBriefing(q1, q2, q3) {
  let key = q2;
  const notes = [];
  if (q2 === '4g') {
    if (q3 === 'non') {
      key = q1 === 'C' ? 'lcd' : 'wifi';
      notes.push('no_network');
    } else if (q3 === 'faible') notes.push('weak');
    else if (q3 === 'nsp') notes.push('check');
  }
  if (q1 === 'C' && q2 === 'wifi') {
    key = q3 === 'oui' || q3 === 'faible' ? '4g' : 'lcd';
    notes.push('wifi_remote');
  }
  if ((q2 === 'fullhd' || q2 === 'lcd') && q3 === 'oui') notes.push('secondary');
  return { key, notes };
}

class STerrainBriefing extends HTMLElement {
  #ac = null;
  #answers = [];
  #current = 1;
  #addedTimer = 0;

  connectedCallback() {
    this.#ac = new AbortController();
    this.addEventListener('click', (event) => this.#onClick(event), { signal: this.#ac.signal });
    const saved = this.#load();
    if (saved) {
      this.#answers = saved;
      this.#syncPressed();
      this.#showResult(false);
    } else {
      this.#show(1, false);
    }
  }

  disconnectedCallback() {
    this.#ac?.abort();
    clearTimeout(this.#addedTimer);
  }

  get #reduce() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  #pane(n) {
    return this.querySelector(`[data-step="${n}"]`);
  }

  #onClick(event) {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    const answer = target.closest('[data-answer]');
    if (answer && this.contains(answer)) return this.#answer(answer);
    if (target.closest('[data-back]')) return this.#back();
    if (target.closest('[data-restart]')) return this.#restart();
    const add = target.closest('[data-add]');
    if (add) this.#add(add);
  }

  #answer(button) {
    const pane = button.closest('[data-step]');
    const n = Number(pane?.dataset.step);
    if (!n) return;
    pane.querySelectorAll('[data-answer]').forEach((b) => b.setAttribute('aria-pressed', String(b === button)));
    this.#answers[n - 1] = button.dataset.answer;
    this.#answers.length = n;
    window.setTimeout(() => (n < 3 ? this.#show(n + 1) : this.#finish()), 160);
  }

  #back() {
    if (this.#current > 1) this.#show(this.#current - 1, true, -1);
  }

  #restart() {
    this.#answers = [];
    this.#syncPressed();
    this.#save(null);
    this.#show(1);
  }

  #finish() {
    this.#save(this.#answers);
    this.#showResult(true);
  }

  #syncPressed() {
    [1, 2, 3].forEach((n) => {
      this.#pane(n)
        ?.querySelectorAll('[data-answer]')
        .forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.answer === this.#answers[n - 1])));
    });
  }

  #slide(el, direction = 1) {
    if (!el || this.#reduce || !el.animate) return;
    el.animate(
      [
        { opacity: 0, transform: `translateX(${24 * direction}px)` },
        { opacity: 1, transform: 'none' },
      ],
      { duration: 250, easing: 'cubic-bezier(0.2, 0, 0, 1)' }
    );
  }

  #track(states) {
    this.querySelectorAll('[data-track]').forEach((item) => {
      const state = states[Number(item.dataset.track) - 1] || 'todo';
      item.dataset.state = state;
      if (state === 'current') item.setAttribute('aria-current', 'step');
      else item.removeAttribute('aria-current');
    });
  }

  #announce(text) {
    const live = this.querySelector('[data-briefing-live]');
    if (live) live.textContent = text;
  }

  #show(n, animate = true, direction = 1) {
    this.#current = n;
    [1, 2, 3].forEach((i) => {
      const pane = this.#pane(i);
      if (pane) pane.hidden = i !== n;
    });
    const area = this.querySelector('[data-result-area]');
    if (area) area.hidden = true;
    const back = this.querySelector('[data-back]');
    if (back) back.hidden = n === 1;

    this.#track([1, 2, 3].map((i) => (i < n ? 'done' : i === n ? 'current' : 'todo')));

    const pane = this.#pane(n);
    const question = pane?.querySelector('[data-question]');
    const template = this.dataset.stepOf || '';
    this.#announce(`${template.replace('{{ number }}', String(n))} ${question?.textContent?.trim() || ''}`.trim());
    if (animate) {
      this.#slide(pane, direction);
      question?.focus({ preventScroll: true });
    }
  }

  #showResult(animate) {
    const [q1, q2, q3] = this.#answers;
    const { key, notes } = resolveBriefing(q1, q2, q3);
    const area = this.querySelector('[data-result-area]');
    if (!area) return;
    const panels = [...area.querySelectorAll('[data-result]')];
    const active = panels.find((p) => p.dataset.result === key) || panels[0];
    if (!active) return;

    [1, 2, 3].forEach((i) => {
      const pane = this.#pane(i);
      if (pane) pane.hidden = true;
    });
    const back = this.querySelector('[data-back]');
    if (back) back.hidden = true;
    panels.forEach((p) => (p.hidden = p !== active));

    const slot = active.querySelector('[data-notes]');
    const holder = area.querySelector('[data-note-holder]');
    this.querySelectorAll('[data-note]').forEach((note) => {
      const target = notes.includes(note.dataset.note) ? slot : holder;
      if (target && note.parentElement !== target) target.append(note);
    });

    this.#renderRecap();
    this.#track(['done', 'done', 'done']);

    area.hidden = false;
    const title = active.querySelector('[data-result-title]');
    this.#announce(title?.textContent?.trim() || '');
    if (animate) {
      this.#slide(area);
      title?.focus({ preventScroll: true });
    }
  }

  #renderRecap() {
    const list = this.querySelector('[data-recap-list]');
    if (!list) return;
    list.replaceChildren();
    this.#answers.forEach((value, i) => {
      const pane = this.#pane(i + 1);
      const tile = [...(pane?.querySelectorAll('[data-answer]') || [])].find((b) => b.dataset.answer === value);
      if (!pane || !tile) return;
      const li = document.createElement('li');
      li.className = 'type-label rounded-card bg-surface-secondary px-3 py-1.5 text-xs text-on-surface';
      li.textContent = `${pane.dataset.stepLabel || ''} : ${tile.dataset.recap || ''}`;
      list.append(li);
    });
  }

  #load() {
    try {
      const raw = window.sessionStorage.getItem(this.dataset.storageKey || 'scx-briefing');
      const saved = raw ? JSON.parse(raw) : null;
      if (!Array.isArray(saved) || saved.length !== 3) return null;
      const [q1, q2, q3] = saved;
      const ok = ['A', 'B', 'C'].includes(q1) && ['fullhd', 'lcd', 'wifi', '4g'].includes(q2) && STEP3_KEYS.includes(q3);
      return ok ? saved : null;
    } catch {
      return null;
    }
  }

  #save(answers) {
    try {
      const key = this.dataset.storageKey || 'scx-briefing';
      if (answers) window.sessionStorage.setItem(key, JSON.stringify(answers));
      else window.sessionStorage.removeItem(key);
    } catch {
      /* storage unavailable: the briefing still works for this visit */
    }
  }

  async #add(button) {
    const variantId = button.dataset.variantId;
    const actions = window.Shopify?.actions;
    if (!variantId || !actions?.updateCart) {
      if (button.dataset.productUrl) window.location.href = button.dataset.productUrl;
      return;
    }
    const error = button.closest('[data-result]')?.querySelector('[data-add-error]');
    if (error) error.hidden = true;
    button.setAttribute('aria-busy', 'true');
    try {
      const result = await actions.updateCart(
        { lines: [{ merchandiseId: variantId, quantity: 1 }] },
        { event: { context: 'product', detail: { source: 's-terrain-briefing' } } }
      );
      const message = result?.userErrors?.[0]?.message;
      if (message) throw new Error(message);
      button.setAttribute('data-added', '');
      clearTimeout(this.#addedTimer);
      this.#addedTimer = window.setTimeout(() => button.removeAttribute('data-added'), 2500);
      await actions.openCart?.();
    } catch (err) {
      if (error) {
        error.textContent = err?.message || error.dataset.defaultMessage || '';
        error.hidden = false;
      }
    } finally {
      button.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('s-terrain-briefing')) {
  customElements.define('s-terrain-briefing', STerrainBriefing);
}
